import { Navigate, useParams, useSearchParams } from 'react-router-dom'

/**
 * `/event/:id/manage` → `/event/:id`, permanently, keeping the tab.
 *
 * ## Why this is not a one-line `<Navigate>`
 *
 * The links people hold are not paths. Measured in the running app by clicking
 * the tabs and reading the address bar:
 *
 *     /event/:id/manage?g=drift&t=deltakere
 *     /event/:id/manage?g=logistikk&t=rom
 *
 * `g` was the stack. The stacks are gone, so it is dropped rather than made an
 * error: an old link should not get worse for carrying it. `t` is kept, because
 * the tab it names still exists.
 *
 * ## The two slugs whose meaning changed
 *
 * - `oversikt` pointed at the admin shell's `DetailsTab`, which was a read-only
 *   mirror of the participant overview and is deleted. The shared Oversikt is
 *   the same content, so dropping `t` lands exactly where the link meant.
 * - `mine-valg` is not in the admin set any more. It drops to Oversikt and
 *   deliberately does NOT switch the viewer into participant mode: a link
 *   should not change which mode the organiser is standing in.
 *
 * Anything else unknown also drops to Oversikt. Never a 403, never a dead end —
 * husregel 1 makes a dead end red, and red is a message to us, not to the
 * person who clicked a link their organiser sent.
 */
const DROPPED = new Set(['oversikt', 'mine-valg'])

export function ManageRedirect() {
  const { id } = useParams<{ id: string }>()
  const [params] = useSearchParams()
  const tab = params.get('t')
  const keep = tab && !DROPPED.has(tab) ? `?t=${encodeURIComponent(tab)}` : ''
  return <Navigate to={`/event/${id}${keep}`} replace />
}
